<template>
  <div class="page-container">
    <Filter :params="params" @search="searchData">
      <template #handle>
        <div class="form-col handle-btns">
          <el-form-item>
            <el-button type="danger" icon="el-icon-plus" @click="addVoice">{{
              $t('smartRouting.voiceLibrary.addExisting')
            }}</el-button>
            <el-button type="danger" icon="el-icon-microphone" @click="autoClone">{{
              $t('smartRouting.voiceLibrary.autoClone')
            }}</el-button>
          </el-form-item>
        </div>
      </template>
    </Filter>

    <Table :tableData="tableDataList" :total="total" @update="updatePage">
      <el-table-column prop="code" :label="$t('smartRouting.voiceLibrary.code')" />
      <el-table-column prop="name" :label="$t('smartRouting.voiceLibrary.name')" />
      <el-table-column prop="tags" :label="$t('smartRouting.voiceLibrary.tags')" width="320">
        <template #default="{ row }">
          <el-tag
            v-for="(tag, index) in displayedTags(row.tags)"
            :key="index"
            size="small"
            type="primary"
            effect="plain"
            class="voice-tag"
          >
            {{ tag }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="createBy" :label="$t('smartRouting.voiceLibrary.creator')" />
      <el-table-column prop="createTime" :label="$t('smartRouting.voiceLibrary.createTime')" />
      <el-table-column prop="updateBy" :label="$t('smartRouting.voiceLibrary.updater')" />
      <el-table-column prop="updateTime" :label="$t('smartRouting.voiceLibrary.updateTime')" />
      <el-table-column :label="$t('smartRouting.voiceLibrary.operation')" fixed="right" width="120" >
        <template #default="{ row }">
          <el-tooltip class="item" effect="dark" :content="$t('smartRouting.voiceLibrary.view')">
            <el-button type="text" size="" icon="el-icon-view" @click="viewVoice(row)" />
          </el-tooltip>
          <el-tooltip class="item" effect="dark" :content="$t('smartRouting.voiceLibrary.edit')">
            <el-button type="text" size="" icon="el-icon-edit" @click="editVoice(row)" />
          </el-tooltip>
          <el-tooltip class="item" effect="dark" :content="$t('smartRouting.voiceLibrary.delete')">
            <el-button type="text" size="" style="color: red;" icon="el-icon-delete" @click="deleteVoice(row)" />
          </el-tooltip>
        </template>
      </el-table-column>
    </Table>

    <!-- 新增音色 -->
    <el-dialog
      :title="$t('smartRouting.voiceLibrary.addVoiceTitle')"
      v-model="addVisible"
      width="600px"
      destroy-on-close
      :append-to-body="true"
    >
      <el-form ref="formRef" :model="form" :rules="formRules" label-width="150px">
        <el-form-item :label="$t('smartRouting.voiceLibrary.name')" prop="name">
          <el-input v-model="form.name" :placeholder="$t('smartRouting.voiceLibrary.namePlaceholder')" />
        </el-form-item>
        <el-form-item :label="$t('smartRouting.voiceLibrary.tags')">
          <el-input v-model="form.tags" :placeholder="$t('smartRouting.voiceLibrary.tagPlaceholder')" />
        </el-form-item>
        <el-form-item :label="$t('smartRouting.voiceLibrary.voiceId')" prop="voiceId">
          <el-input v-model="form.voiceId" :placeholder="$t('smartRouting.voiceLibrary.voiceIdPlaceholder')" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="addVisible = false">{{ $t('smartRouting.voiceLibrary.cancel') }}</el-button>
        <el-button type="danger" @click="submitAdd">{{ $t('smartRouting.voiceLibrary.confirm') }}</el-button>
      </template>
    </el-dialog>

    <!-- 自动复刻音色 -->
    <el-dialog
      :title="cloneTitle"
      v-model="cloneVisible"
      width="600px"
      destroy-on-close
      :append-to-body="true"
      @closed="onCloneClosed"
    >
      <div class="clone-steps">
        <div class="step" :class="{ active: cloneStep === 1 }">
          <span class="step-index">1</span>
          <span class="step-title">{{ $t('smartRouting.voiceLibrary.stepUpload') }}</span>
        </div>
        <div class="step-line" />
        <div class="step" :class="{ active: cloneStep === 2 }">
          <span class="step-index">2</span>
          <span class="step-title">{{ $t('smartRouting.voiceLibrary.stepClone') }}</span>
        </div>
      </div>

      <!-- 步骤 1：上传音频 -->
      <div v-if="cloneStep === 1">
        <p class="clone-tip">{{ $t('smartRouting.voiceLibrary.uploadAudioTip') }}</p>
        <p class="clone-warning">{{ $t('smartRouting.voiceLibrary.cloneRiskWarning') }}</p>
        <el-checkbox v-model="cloneAuthorized" class="clone-auth-checkbox">
          {{ $t('smartRouting.voiceLibrary.authConfirm') }}
        </el-checkbox>
        <div class="upload-box" @click="triggerFileSelect">
          <span class="upload-placeholder" :class="{ selected: cloneAudioFile }">
            {{ displayedPath(cloneAudioFile) || $t('smartRouting.voiceLibrary.selectFile') }}
          </span>
          <el-button size="small" type="primary" plain @click.stop="triggerFileSelect">
            {{ $t('smartRouting.voiceLibrary.selectFile') }}
          </el-button>
        </div>
        <input
          ref="fileInputRef"
          type="file"
          accept=".wav,.mp3,.m4a"
          style="display: none"
          @change="onFileChange"
        />
      </div>

      <!-- 步骤 2：填写信息并保存 -->
      <div v-else-if="form.voiceId && cloneStep === 2">
        <p class="clone-tip">{{ $t('smartRouting.voiceLibrary.cloneCompleteTip') }}</p>
        <p class="clone-warning">{{ $t('smartRouting.voiceLibrary.aiSynthesizedTip') }}</p>
        <el-form ref="formRef" :model="form" :rules="formRules" label-width="150px">
          <el-form-item :label="$t('smartRouting.voiceLibrary.audioFile')">
            <!-- <el-input v-model="form.refAudioPath" disabled /> -->
            {{ displayedPath(form.refAudioPath) }}
          </el-form-item>
          <el-form-item :label="$t('smartRouting.voiceLibrary.voiceId')">
            <el-input v-model="form.voiceId" disabled />
          </el-form-item>
          <el-form-item :label="$t('smartRouting.voiceLibrary.name')" prop="name">
            <el-input v-model="form.name" :placeholder="$t('smartRouting.voiceLibrary.namePlaceholder')" />
          </el-form-item>
          <el-form-item :label="$t('smartRouting.voiceLibrary.tags')">
            <el-input v-model="form.tags" :placeholder="$t('smartRouting.voiceLibrary.tagPlaceholder')" />
          </el-form-item>
        </el-form>
      </div>
      <div v-else>
        <p class="clone-tip">{{ $t('smartRouting.voiceLibrary.autoCloning') }}</p>
        <el-progress :stroke-width="15" :percentage="progress" />
      </div>

      <template #footer >
        <el-button @click="onCloneClosed">{{ $t('smartRouting.voiceLibrary.cancel') }}</el-button>
        <el-button v-if="cloneStep === 1" :disabled="!cloneAuthorized" type="danger" @click="cloneNext">
          {{ $t('smartRouting.voiceLibrary.nextStep') }}
        </el-button>
        <template v-else-if="form.voiceId && cloneStep === 2">
          <el-button @click="clonePrev">{{ $t('smartRouting.voiceLibrary.prevStep') }}</el-button>
          <el-button type="danger" @click="submitClone">{{ $t('smartRouting.voiceLibrary.save') }}</el-button>
        </template>
      </template>
    </el-dialog>

    <!-- 编辑音色 -->
    <el-dialog
      :title="$t('smartRouting.voiceLibrary.editVoiceTitle')"
      v-model="editVisible"
      width="600px"
      destroy-on-close
      :append-to-body="true"
    >
      <el-form ref="formRef" :model="form" :rules="formRules" label-width="150px">
        <el-form-item :label="$t('smartRouting.voiceLibrary.name')" prop="name">
          <el-input v-model="form.name" :placeholder="$t('smartRouting.voiceLibrary.namePlaceholder')" />
        </el-form-item>
        <el-form-item :label="$t('smartRouting.voiceLibrary.tags')">
          <el-input v-model="form.tags" :placeholder="$t('smartRouting.voiceLibrary.tagPlaceholder')" />
        </el-form-item>
        <el-form-item :label="$t('smartRouting.voiceLibrary.audioFile')">
          <!-- <el-input v-model="form.refAudioPath" disabled /> -->
          {{ displayedPath(form.refAudioPath) }}
        </el-form-item>
        <el-form-item :label="$t('smartRouting.voiceLibrary.voiceId')">
          <el-input v-model="form.voiceId" disabled />
          <div class="form-tip">{{ $t('smartRouting.voiceLibrary.autoGenerateTip') }}</div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="editVisible = false">{{ $t('smartRouting.voiceLibrary.cancel') }}</el-button>
        <el-button type="danger" @click="submitEdit">{{ $t('smartRouting.voiceLibrary.save') }}</el-button>
      </template>
    </el-dialog>

    <!-- 查看音色 -->
    <el-dialog
      :title="$t('smartRouting.voiceLibrary.viewVoiceTitle')"
      v-model="viewVisible"
      width="600px"
      destroy-on-close
      :append-to-body="true"
    >
      <el-form label-width="150px" class="view-form">
        <!-- <el-form-item :label="$t('smartRouting.voiceLibrary.code')">
          <el-input v-model="detail.code" disabled />
        </el-form-item> -->
        <el-form-item :label="$t('smartRouting.voiceLibrary.name')">
          <el-input v-model="detail.name" disabled />
        </el-form-item>
        <el-form-item :label="$t('smartRouting.voiceLibrary.tags')">
          <el-tag
            v-for="(tag, index) in displayedTags(detail.tags)"
            :key="index"
            size="small"
            type="primary"
            effect="plain"
            class="voice-tag"
          >
            {{ tag }}
          </el-tag>
        </el-form-item>
        <el-form-item :label="$t('smartRouting.voiceLibrary.audioFile')">
          <!-- <el-input v-model="detail.refAudioPath" disabled /> -->
          {{ displayedPath(detail.refAudioPath) }}
        </el-form-item>
        <el-form-item :label="$t('smartRouting.voiceLibrary.voiceId')">
          <el-input v-model="detail.voiceId" disabled />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="viewVisible = false">{{ $t('smartRouting.actionLibrary.close') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { common } from '@/common/mixin';
import Table from '@/components/table/index';
import Filter from '@/components/table/filter';
import { ElMessageBox, ElMessage } from 'element-plus';
import { reactive, toRefs, ref, computed } from '@vue/reactivity';
import { useI18n } from 'vue-i18n';
import dayjs from 'dayjs';
import * as Api from '@/api/smartRouting/voice';
import { ossUpload } from '@/utils/oss'

export default {
  components: {
    Table,
    Filter
  },
  mixins: [common],
  setup() {
    const { t } = useI18n();
    const formRef = ref(null);
    const fileInputRef = ref(null);
    const cloneTimer = ref(null);

    const params = [
      {
        key: 'name',
        value: '',
        label: t('smartRouting.voiceLibrary.name'),
        placeholder: t('smartRouting.voiceLibrary.enterName')
      },
      {
        key: 'tag',
        value: '',
        label: t('smartRouting.voiceLibrary.tag'),
        placeholder: t('smartRouting.voiceLibrary.enterTag')
      }
    ];

    const state = reactive({
      query: {},
      page: 1,
      size: 10,
      total: 0,
      tableDataList: [],
      addVisible: false,
      editVisible: false,
      viewVisible: false,
      cloneVisible: false,
      cloneStep: 1,
      cloneAudioFile: '',
      cloneGeneratedVoiceId: '',
      cloneAuthorized: false,
      form: {
        code: '',
        name: '',
        tags: '',
        voiceId: '',
        refAudioPath: ''
      },
      taskId: '',
      progress: 0,
      detail: {}
    });

    const cloneTitle = computed(() => {
      const base = t('smartRouting.voiceLibrary.cloneVoiceTitle');
      const stepText =
        state.cloneStep === 1
          ? t('smartRouting.voiceLibrary.stepUpload')
          : t('smartRouting.voiceLibrary.stepClone');
      return `${base} · ${stepText}`;
    });

    const formRules = {
      name: {
        required: true,
        message: t('smartRouting.voiceLibrary.nameRequired'),
        trigger: 'blur'
      },
      voiceId: {
        required: true,
        message: t('smartRouting.voiceLibrary.voiceIdRequired'),
        trigger: 'blur'
      }
    };

    const displayedTags = (tags) => {
      return tags?.split(/[,，]/);
    };

    const displayedPath = (path) => {
      return path?.split('/').pop()
    };

    const hasMoreTags = (tags) => {
      return (tags || []).length > 3;
    };

    const resetForm = () => {
      state.form = {
        code: '',
        name: '',
        tags: '',
        voiceId: '',
        refAudioPath: ''
      };
    };

    const getData = async () => {
      try {
        const res = await Api.reqVoiceList({
          ...state.query,
          page: state.page,
          size: state.size,
        })
        state.tableDataList = res.records || []
        state.total = Number(res.total || 0)
      } catch (error) {
        console.error(error)
      }
    }

    const searchData = (data) => {
      state.query = data;
      state.page = 1;
      getData();
    };

    const updatePage = (page, size) => {
      state.page = page || 1;
      state.size = size || 10;
      getData();
    };

    // 新增已有音色
    const addVoice = () => {
      resetForm();
      state.form.source = 'INPUT'
      state.addVisible = true;
    };

    const submitAdd = async () => {
      try {
        await formRef.value?.validate();
        await Api.reqVoiceAdd(state.form);
        state.addVisible = false;
        getData();
        ElMessage({
          showClose: true,
          type: 'success',
          message: t('activity.list.submitSuccess')
        });
      } catch (error) {
        console.error(error);
      }
    };

    // 自动复刻音色
    const autoClone = () => {
      state.cloneStep = 1;
      state.cloneAudioFile = '';
      state.cloneGeneratedVoiceId = '';
      state.cloneAuthorized = false;
      resetForm();
      state.cloneVisible = true;
    };

    const onCloneClosed = () => {
      state.cloneVisible = false;
      state.cloneStep = 1;
      state.cloneAudioFile = '';
      state.cloneGeneratedVoiceId = '';
      state.cloneAuthorized = false;
      if (cloneTimer.value) {
        clearTimeout(cloneTimer.value);
        cloneTimer.value = null;
      }
      if (fileInputRef.value) {
        fileInputRef.value.value = '';
      }
    };

    const triggerFileSelect = () => {
      fileInputRef.value?.click();
    };

    const onFileChange = async (e) => {
      const file = e.target.files?.[0];
      if (file) {
        state.cloneAudioFile = 'smart/voice/' + dayjs().format('YYYY/MM/DD/') + file.name;

        const fileUrl = await ossUpload(file.name, file, {
          path: 'smart/voice/' + dayjs().format('YYYY/MM/DD'),
          timestamp: false,
        })
        console.log('onFileChange ->', state.cloneAudioFile, fileUrl);
      }
    };

    const cloneNext = async () => {
      try {
        if (!state.cloneAudioFile) {
          ElMessage({
            showClose: true,
            type: 'warning',
            message: t('smartRouting.voiceLibrary.fileRequired')
          });
          return;
        }
        if (!state.cloneAuthorized) {
          ElMessage({
            showClose: true,
            type: 'warning',
            message: t('smartRouting.voiceLibrary.authRequired')
          });
          return;
        }
        state.progress = 0;
        const task = await Api.reqVoiceClone({
          refAudioPath: state.cloneAudioFile
        });
        if (cloneTimer.value) {
          clearTimeout(cloneTimer.value);
          cloneTimer.value = null;
        }
        state.taskId = task.taskId;
        state.cloneStep = 2;
        let elapsed = 0;
        const interval = 1000;
        const maxTime = 3 * 60 * 1000;
        const checkProgress = async () => {
          try {
            const res = await Api.reqVoiceCloneProgress(task.taskId);
            state.progress = res.progress;
            if (res && res.voiceId) {
              state.cloneGeneratedVoiceId = res.voiceId;
              resetForm();
              state.form.refAudioPath = state.cloneAudioFile;
              state.form.voiceId = state.cloneGeneratedVoiceId;

              cloneTimer.value = null;
              return;
            }
            elapsed += interval;
            if (elapsed >= maxTime) {
              cloneTimer.value = null;
              ElMessage({
                showClose: true,
                type: 'error',
                message: '音色复刻超时，请重试'
              });
              return;
            }
            cloneTimer.value = setTimeout(checkProgress, interval);
          } catch (error) {
            console.error(error);
            cloneTimer.value = setTimeout(checkProgress, interval);
          }
        };
        cloneTimer.value = setTimeout(checkProgress, 0);
      } catch (error) {
        console.error(error);
      }
    };
    const clonePrev = () => {
      state.cloneStep = 1;
      resetForm();
    };

    const submitClone = async () => {
      try {
        await formRef.value?.validate();
        state.form.taskId = state.taskId;
        await Api.reqVoiceSave(state.taskId, state.form)
        state.cloneVisible = false;
        getData();
        ElMessage({
          showClose: true,
          type: 'success',
          message: t('activity.list.submitSuccess')
        });
      } catch (error) {
        console.error(error);
      }
    };

    // 编辑
    const editVoice = (row) => {
      resetForm();
      state.form = {
        code: row.code,
        name: row.name,
        tags: row.tags || '',
        voiceId: row.voiceId || '',
        refAudioPath: row.refAudioPath || ''
      };
      state.editVisible = true;
    };

    const submitEdit = async () => {
      try {
        await formRef.value?.validate();
        await Api.reqVoiceEdit(state.form);
        state.editVisible = false;
        getData();
        ElMessage({
          showClose: true,
          type: 'success',
          message: t('activity.list.submitSuccess')
        });
      } catch (error) {
        console.error(error);
      }
    };

    // 查看
    const viewVoice = (row) => {
      state.detail = { ...row };
      state.viewVisible = true;
    };

    // 删除
    const deleteVoice = async (row) => {
      try {
        await ElMessageBox.confirm(
          t('smartRouting.voiceLibrary.confirmDelete'),
          t('smartRouting.voiceLibrary.prompt'),
          {
            confirmButtonText: t('activity.list.confirm1'),
            cancelButtonText: t('activity.list.cancel'),
            type: 'warning'
          }
        );
        await Api.reqVoiceDelete(row.code);
        getData();
        ElMessage({
          showClose: true,
          type: 'success',
          message: t('smartRouting.voiceLibrary.deleteSuccess')
        });
      } catch (error) {
        console.error(error);
      }
    };

    // 页面初始化
    getData();

    return {
      ...toRefs(state),
      params,
      formRef,
      fileInputRef,
      formRules,
      cloneTitle,
      displayedTags,
      displayedPath,
      hasMoreTags,
      getData,
      searchData,
      updatePage,
      addVoice,
      submitAdd,
      autoClone,
      onCloneClosed,
      triggerFileSelect,
      onFileChange,
      cloneNext,
      clonePrev,
      submitClone,
      editVoice,
      submitEdit,
      viewVoice,
      deleteVoice
    };
  }
};
</script>

<style lang="scss" scoped>
.page-container {
  height: 100%;
  padding: 50px 10px 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.handle-btns {
  margin-left: auto;
  margin-right: 0;
}

.voice-tag {
  border-radius: 12px;
  margin-right: 6px;
}

.clone-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #909399;
}

.step.active {
  color: #409eff;
}

.step-index {
  width: 28px;
  height: 28px;
  line-height: 28px;
  text-align: center;
  border-radius: 50%;
  background-color: #f0f2f5;
  color: #909399;
  font-size: 14px;
  margin-bottom: 6px;
}

.step.active .step-index {
  background-color: #409eff;
  color: #fff;
}

.step-line {
  width: 120px;
  height: 1px;
  background-color: #dcdfe6;
  margin: 0 16px;
  margin-bottom: 24px;
}

.clone-tip {
  color: #606266;
  font-size: 14px;
  margin-bottom: 16px;
}

.clone-warning {
  color: #f56c6c;
  font-size: 14px;
  margin-bottom: 12px;
}

.clone-auth-checkbox {
  display: flex;
  align-items: flex-start;
  white-space: normal;
  margin-bottom: 16px;
  :deep(.el-checkbox__input) {
    margin-top: 2px;
  }
  :deep(.el-checkbox__label) {
    white-space: normal;
  }
}

.upload-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  background-color: #fafafa;
  cursor: pointer;
}

.upload-box:hover {
  border-color: #409eff;
}

.upload-placeholder {
  color: #c0c4cc;
}

.upload-placeholder.selected {
  color: #606266;
  color: #67c23a;
}

.form-tip {
  color: #67c23a;
  font-size: 12px;
  margin-top: 6px;
}

.view-form :deep(.el-input.is-disabled .el-input__inner) {
  // background-color: #fff;
  color: #606266;
}
</style>
